import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WidgetCard from './WidgetCard';
import { useWidgetData } from './useWidgetData';

interface ExpiryItem {
  id: number;
  pharmacyNum: string | null;
  materialName: string;
  serialNumber: string | null;
  /** YYYY-MM-DD */
  expiryDate: string;
  /** 0 - срок истекает сегодня */
  daysLeft: number;
  quantity: number;
  totalSum: number;
}

interface ExpiryResponse {
  scope: string;
  windowDays: number;
  summary: { today: number; week: number; month: number; totalSum: number };
  items: ExpiryItem[];
}

const ACCENT = '#d97706';

const dateLabel = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const Counter: React.FC<{ label: string; value: number; color: string; bg: string }> = ({ label, value, color, bg }) => (
  <Box sx={{ flex: 1, minWidth: 0, bgcolor: bg, borderRadius: '10px', px: 1.5, py: 0.9 }}>
    <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.2 }}>{label}</Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.3 }}>{value}</Typography>
  </Box>
);

const chipFor = (days: number) => {
  if (days <= 0) return { label: 'Сегодня', bg: '#fef2f2', color: '#b91c1c' };
  if (days <= 7) return { label: `Осталось ${days} дн.`, bg: '#fff7ed', color: '#c2410c' };
  return { label: `Осталось ${days} дн.`, bg: '#fefce8', color: '#a16207' };
};

/** Виджет «Журнал сроков»: позиции электронного журнала с истекающим сроком годности. Право: home.journal.expiry.view (+ .limit) */
const ExpiryJournalWidget: React.FC = () => {
  const { data, loading, error, reload } = useWidgetData<ExpiryResponse>('/api/v2/home/journal/expiry');
  const items = data?.items || [];
  const s = data?.summary;
  const showPharmacy = data ? data.scope !== 'own_pharmacy' : false;

  return (
    <WidgetCard
      title="Журнал сроков"
      icon={<EventBusyOutlinedIcon />}
      accent={ACCENT}
      caption={data ? `ближайшие ${data.windowDays} дней` : undefined}
      loading={loading}
      error={error}
      onRefresh={reload}
    >
      {data && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1.25 }}>
            <Counter label="Срок сегодня" value={s?.today || 0} color="#b91c1c" bg="#fef2f2" />
            <Counter label="В течение 7 дней" value={s?.week || 0} color="#c2410c" bg="#fff7ed" />
            <Counter label="В течение 30 дней" value={s?.month || 0} color="#a16207" bg="#fefce8" />
          </Box>
          {items.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, color: '#64748b', borderTop: '1px solid #f1f5f9' }}>
              <TaskAltRoundedIcon sx={{ color: '#10b981' }} />
              <Typography sx={{ fontSize: 13 }}>Позиций с истекающим сроком нет</Typography>
            </Box>
          ) : (
            items.map(it => {
              const chip = chipFor(it.daysLeft);
              return (
                <Box key={it.id} sx={{ px: 2, py: 0.9, display: 'flex', alignItems: 'center', gap: 1.5, borderTop: '1px solid #f1f5f9', '&:hover': { bgcolor: '#f8fafc' } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }} noWrap>{it.materialName}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }} noWrap>
                      {showPharmacy && it.pharmacyNum ? `Аптека №${it.pharmacyNum} · ` : ''}
                      до {dateLabel(it.expiryDate)} · {it.quantity} уп.{it.serialNumber ? ` · серия ${it.serialNumber}` : ''}
                    </Typography>
                  </Box>
                  <Chip size="small" label={chip.label} sx={{ height: 22, flexShrink: 0, fontWeight: 600, bgcolor: chip.bg, color: chip.color }} />
                </Box>
              );
            })
          )}
        </Box>
      )}
    </WidgetCard>
  );
};

export default ExpiryJournalWidget;
